import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAppSelector } from './store/hooks';
import { selectAdminIsAuthenticated } from './features/adminAuth/selectors/adminAuth.selectors';
import { ToastProvider } from './contexts/ToastContext';
import { NotificationToastProvider } from './contexts/NotificationToastContext';

// Lazy loaded pages
const AdminSignIn = React.lazy(() => import('./pages/admin/auth/AdminSignIn'));
const AdminSignUp = React.lazy(() => import('./pages/admin/auth/AdminSignUp'));
const AdminVerifyEmail = React.lazy(() => import('./pages/admin/auth/AdminVerifyEmail'));
const AdminVerifySuccess = React.lazy(() => import('./pages/admin/auth/AdminVerifySuccess'));
const AdminProfileSetup = React.lazy(() => import('./pages/admin/auth/AdminProfileSetup'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminOverviewPage = React.lazy(() => import('./pages/admin/AdminOverviewPage'));
const AdminPerformancePage = React.lazy(() => import('./pages/admin/AdminPerformancePage'));
const AdminReportedIssuesPage = React.lazy(() => import('./pages/admin/AdminReportedIssuesPage'));
const AdminUsersPage = React.lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminUsersDetailPage = React.lazy(() => import('./pages/admin/AdminUsersDetailPage'));
const AdminListingsPage = React.lazy(() => import('./pages/admin/AdminListingsPage'));
const AdminListingsActivityDetailPage = React.lazy(() => import('./pages/admin/AdminListingsActivityDetailPage'));
const AdminRequestsPage = React.lazy(() => import('./pages/admin/AdminRequestsPage'));
const AdminChatPage = React.lazy(() => import('./pages/admin/AdminChatPage'));
const AdminHistoryPage = React.lazy(() => import('./pages/admin/AdminHistoryPage'));
const AdminExportPage = React.lazy(() => import('./pages/admin/AdminExportPage'));
const AdminSettingsPage = React.lazy(() => import('./pages/admin/AdminSettingsPage'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAppSelector(selectAdminIsAuthenticated);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function GuestRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAppSelector(selectAdminIsAuthenticated);
  if (isAuthenticated) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <NotificationToastProvider>
          <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div></div>}>
            <Routes>
              <Route path="/login" element={<GuestRoute><AdminSignIn /></GuestRoute>} />
              <Route path="/register" element={<GuestRoute><AdminSignUp /></GuestRoute>} />
              <Route path="/verify-email" element={<GuestRoute><AdminVerifyEmail /></GuestRoute>} />
              <Route path="/verify-success" element={<GuestRoute><AdminVerifySuccess /></GuestRoute>} />
              <Route path="/profile-setup" element={<ProtectedRoute><AdminProfileSetup /></ProtectedRoute>} />

              <Route element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>}>
                <Route index element={<AdminOverviewPage />} />
                <Route path="performance" element={<AdminPerformancePage />} />
                <Route path="reported-issues" element={<AdminReportedIssuesPage />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="users/:slug" element={<AdminUsersDetailPage />} />
                <Route path="users/:slug/access" element={<AdminUsersDetailPage />} />
                <Route path="listings" element={<AdminListingsPage />} />
                <Route path="listings/:slug" element={<AdminListingsActivityDetailPage />} />
                <Route path="requests" element={<AdminRequestsPage />} />
                <Route path="chat" element={<AdminChatPage />} />
                <Route path="history" element={<AdminHistoryPage />} />
                <Route path="export" element={<AdminExportPage />} />
                <Route path="settings" element={<AdminSettingsPage />} />
                <Route path="*" element={<AdminOverviewPage />} />
              </Route>
            </Routes>
          </React.Suspense>
        </NotificationToastProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;
